"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@kkeut/lib/firebase/client";
import { usePageParam } from "@kkeut/ui/hooks";
import { ChevronRight, ChevronLeft } from "lucide-react";

/* ─────────────────────────── 인라인 유틸 ─────────────────────────── */
function getLocaleString(value, locale) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value?.[locale] ?? value?.["ko"] ?? Object.values(value || {})[0] ?? "";
}

function withLocaleHref(href = "/", locale = "ko") {
  const EXTERNAL = /^(https?:)?\/\//i;
  const LOCALE_RE = /^\/(ko|en|ja|zh)(?=\/|$)/;
  if (EXTERNAL.test(href) || href.startsWith("tel:") || href.startsWith("mailto:")) return href;
  let p = href.startsWith("/") ? href : `/${href}`;
  p = p.replace(LOCALE_RE, "") || "/";
  if (locale && locale !== "ko") return `/${locale}${p === "/" ? "" : p}`;
  return p || "/";
}

/* ================= 설정 ================= */
const ITEMS_PER_PAGE = 12;
const SUPPORTED = ["ko", "en", "ja", "zh", "th"];

/* ================= UI texts ================= */
const UI = {
  bannerSub: {
    ko: "시술안내/이벤트",
    en: "Treatments & Events",
    ja: "施術案内 / イベント",
    zh: "疗程指南 / 活动",
    th: "ทรีตเมนต์/อีเวนต์",
  },
  heading: {
    ko: "시술",
    en: "Treatments",
    ja: "施術",
    zh: "疗程",
    th: "ทรีตเมนต์",
  },
  subline: {
    ko: "원하는 시술을 만나보세요!",
    en: "Find the treatment you want!",
    ja: "ご希望の施術を見つけよう！",
    zh: "找到你想要的疗程！",
    th: "ค้นหาทรีตเมนต์ที่คุณต้องการ!",
  },
  sortRecent: {
    ko: "최근등록순",
    en: "Newest",
    ja: "新着順",
    zh: "最新",
    th: "ใหม่ล่าสุด",
  },
  sortHigh: {
    ko: "높은가격순",
    en: "Price: High → Low",
    ja: "価格：高い順",
    zh: "价格：高到低",
    th: "ราคา: สูง → ต่ำ",
  },
  sortLow: {
    ko: "낮은가격순",
    en: "Price: Low → High",
    ja: "価格：安い順",
    zh: "价格：低到高",
    th: "ราคา: ต่ำ → สูง",
  },
  searchPh: {
    ko: "검색어를 입력해주세요.",
    en: "Type to search…",
    ja: "検索ワードを入力してください。",
    zh: "请输入搜索词。",
    th: "พิมพ์เพื่อค้นหา…",
  },
  noResults: {
    ko: "검색 결과가 없습니다.",
    en: "No results found.",
    ja: "該当する結果はありません。",
    zh: "没有找到结果。",
    th: "ไม่พบผลลัพธ์",
  },
  allCategories: {
    ko: "전체 카테고리",
    en: "All Categories",
    ja: "すべてのカテゴリ",
    zh: "全部分类",
    th: "หมวดหมู่ทั้งหมด",
  },
  openAll: {
    ko: "전체보기",
    en: "View all",
    ja: "すべて表示",
    zh: "查看全部",
    th: "ดูทั้งหมด",
  },
  close: {
    ko: "닫기",
    en: "Close",
    ja: "閉じる",
    zh: "关闭",
    th: "ปิด",
  },
  catSearchPh: {
    ko: "카테고리 검색",
    en: "Search categories…",
    ja: "カテゴリ検索",
    zh: "搜索分类",
    th: "ค้นหาหมวดหมู่…",
  },
  prevPage: {
    ko: "이전 페이지",
    en: "Previous page",
    ja: "前のページ",
    zh: "上一页",
    th: "หน้าก่อนหน้า",
  },
  nextPage: {
    ko: "다음 페이지",
    en: "Next page",
    ja: "次のページ",
    zh: "下一页",
    th: "หน้าถัดไป",
  },
  pageNumber: {
    ko: "페이지",
    en: "Page",
    ja: "ページ",
    zh: "页",
    th: "หน้า",
  },
};

const t = (obj, locale) => obj?.[locale] ?? obj?.ko ?? "";

/* ===== helpers ===== */
const asArray = (v) => (Array.isArray(v) ? v : []);

/* 상세옵션 노출 기간(visibility) 체크 — 이벤트 기간 종료된 옵션은 대표가(최저가) 계산에서 제외.
   (clinic-price-detail 의 isDetailVisibleNow 와 동일 규칙) */
const toDateMaybe = (x) => {
  if (!x) return null;
  if (typeof x.toDate === "function") return x.toDate();
  if (typeof x === "object" && typeof x.seconds === "number") return new Date(x.seconds * 1000);
  if (typeof x === "string") {
    const d = new Date(x);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};
const parseHHMM = (s) => {
  if (!s || !/^\d{2}:\d{2}$/.test(s)) return null;
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
};
const getKstNumbers = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Seoul", hour: "2-digit", minute: "2-digit", hour12: false, weekday: "short",
  }).formatToParts(date);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const wd = (parts.find((p) => p.type === "weekday")?.value ?? "Sun").slice(0, 3);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return { dow: map[wd] ?? 0, curMin: h * 60 + m };
};
const isInWindow = (win, kst) => {
  const days = Array.isArray(win?.days) && win.days.length ? win.days : [0, 1, 2, 3, 4, 5, 6];
  const s = parseHHMM(win?.start) ?? 0;
  const e = parseHHMM(win?.end) ?? 24 * 60 - 1;
  if (e > s) return days.includes(kst.dow) && kst.curMin >= s && kst.curMin < e;
  const inTodayLate = days.includes(kst.dow) && kst.curMin >= s;
  const prevDow = (kst.dow + 6) % 7;
  const inAfterMidnight = days.includes(prevDow) && kst.curMin < e;
  return inTodayLate || inAfterMidnight;
};
const isWithinExposureNow = (d) => {
  const v = d?.visibility || {};
  if (!v.enabled) return true; // 노출 기간 미설정 → 항상 노출
  const now = new Date();
  const start = toDateMaybe(v.displayStart);
  const end = toDateMaybe(v.displayEnd);
  if (start && now < start) return false; // 시작 전
  if (end && now > end) return false; // 종료 후 (← 이벤트 기간 종료 시 대표가에서 제외)
  const wins = Array.isArray(v.dailyWindows) ? v.dailyWindows : [];
  if (wins.length === 0) return true;
  const kst = getKstNumbers(now);
  return wins.some((w) => isInWindow(w, kst));
};

const visDetails = (ds) => {
  const arr = asArray(ds);
  const visible = arr.filter((d) => d?.isVisible);
  const active = visible.filter((d) => isWithinExposureNow(d)); // 노출 중(기간 내)인 옵션
  // 노출 중 옵션 우선. 없으면 기존 동작(노출 옵션 전체)로 fallback → 비노출 옵션이 대표가로 튀는 것 방지
  return active.length ? active : visible.length ? visible : arr;
};

const firstDetail = (ds) => visDetails(ds)[0] || null;

const parseKrw = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const m = v.match(/[\d,.]+/g);
    if (!m) return NaN;
    const joined = m.join("").replace(/,/g, "");
    const n = Number(joined);
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

const pickPriceField = (d) => d?.discountPrice ?? d?.salePrice ?? d?.price ?? null;

const minDiscount = (ds) => {
  const nums = visDetails(ds)
    .map((d) => pickPriceField(d))
    .map(parseKrw)
    .filter((n) => Number.isFinite(n) && n > 0);
  return nums.length ? Math.min(...nums) : null;
};

const formatPriceKRW = (v, locale) => {
  if (!Number.isFinite(v) || v <= 0) return "-";
  const n = Number(v).toLocaleString("ko-KR");
  switch (locale) {
    case "en": return `₩${n} ~`;
    case "ja": return `₩${n} ～`;
    case "zh": return `₩${n} 起`;
    case "th": return `₩${n} ขึ้นไป`;
    default: return `${n}원 ~`;
  }
};

const getCatLabel = (cat, locale) => {
  if (!cat) return "";
  const short = cat.shortName || cat.shortLabel;
  return getLocaleString(short ?? cat.name, locale);
};

const findCatIdByAnyName = (cats, nameStr) => {
  if (!nameStr) return null;
  for (const c of cats) {
    for (const lg of SUPPORTED) {
      if (getLocaleString(c.name, lg) === nameStr) return c.id;
      if (c.shortName && getLocaleString(c.shortName, lg) === nameStr) return c.id;
    }
    if (typeof c.name === "string" && c.name === nameStr) return c.id;
  }
  return null;
};

const pickImageByLocale = (item, locale) => {
  const i18n = item?.imageByLang;
  const fromI18n =
    i18n && typeof i18n === "object"
      ? i18n[locale] || i18n.ko || Object.values(i18n).find(Boolean)
      : null;
  return fromI18n || item?.image || "/assets/placeholder.png";
};

/* ================= Mobile Category Sheet ================= */
function MobileCategorySheet({ open, onClose, categories, selectedCatId, lang, onPick }) {
  const [q, setQ] = useState("");
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setTimeout(() => {
      const el = panelRef.current?.querySelector?.("input");
      el?.focus?.();
    }, 50);
  }, [open]);

  const filteredCats = useMemo(() => {
    const key = q.trim().toLowerCase();
    if (!key) return categories;
    return categories.filter((c) => {
      const label = getCatLabel(c, lang).toLowerCase();
      const ko = getLocaleString(c.name, "ko").toLowerCase();
      return label.includes(key) || ko.includes(key);
    });
  }, [q, categories, lang]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] lg:hidden">
      <button
        type="button"
        aria-label="close overlay"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div
        ref={panelRef}
        className="absolute bottom-0 left-0 right-0 max-h-[78vh] rounded-t-3xl bg-white shadow-2xl flex flex-col"
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 pt-4 pb-3 border-b flex-none">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-base font-bold text-[#181818]">{t(UI.allCategories, lang)}</p>
              <p className="text-xs text-[#555] mt-0.5">{categories.length} {lang === "en" ? "items" : "개"}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border px-3 py-1.5 text-sm hover:bg-gray-50"
            >
              {t(UI.close, lang)}
            </button>
          </div>
          <div className="mt-3">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t(UI.catSearchPh, lang)}
              className="w-full rounded-2xl border bg-white px-4 py-3 text-sm outline-none focus:border-[#181818]"
            />
          </div>
        </div>

        <div className="px-3 py-3 overflow-y-auto flex-1">
          {filteredCats.length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-500">{t(UI.noResults, lang)}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filteredCats.map((cat) => {
                const full = getCatLabel(cat, lang);
                const isActive = selectedCatId === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      onPick?.(cat.id);
                      onClose?.();
                    }}
                    className={`rounded-2xl border px-4 py-3 text-sm font-medium text-left transition
                      ${isActive
                        ? "border-[#FFC0CB] bg-[#FFC0CB] text-[#181818] shadow-sm font-bold"
                        : "border-[#e6e6e6] bg-white text-[#444] hover:border-[#bbb] hover:bg-gray-50"
                      }`}
                    title={full}
                  >
                    <span className="block line-clamp-2">{full}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= Main Component ================= */
/**
 * @param {object} props
 * @param {string} props.branchId - Firestore 지점 키 (예: "bssm", "aybk")
 * @param {string} props.locale - useLocale() 에서 resolve된 locale (wrapper에서 전달)
 */
export default function ClinicPriceList({
  branchId,
  locale: localeProp,
  initialCategories = [],
  initialItems = [],
  initialSelectedCatId = null,
}) {
  const hasSSRData = initialCategories.length > 0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const pathLocale = /^\/(en|ja|zh|th)(?=\/|$)/.exec(pathname || "")?.[1] || null;
  const lang = pathLocale || localeProp || "ko";

  const rawCatId = searchParams.get("catId");
  const rawCatName = searchParams.get("category")
    ? decodeURIComponent(searchParams.get("category"))
    : null;

  const [categories, setCategories] = useState(hasSSRData ? initialCategories : []);
  const [selectedCatId, setSelectedCatId] = useState(hasSSRData ? (initialSelectedCatId || "ALL") : null);

  const [items, setItems] = useState(hasSSRData ? initialItems : []);
  const [search, setSearch] = useState("");
  const [sortType, setSortType] = useState("recent");
  const [catSheetOpen, setCatSheetOpen] = useState(false);


  // 서버 렌더 단계에서도 ?page 를 반영한다. 예전에는 항상 1 페이지로 시작해서,
  // ?page=2 로 들어와도 첫 HTML 은 1 페이지였다 — 13번째 이후 시술이 어떤
  // 주소로도 초기 HTML 에 나오지 않아 검색엔진이 찾을 수 없었다.
  const rawPage = Number.parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = usePageParam("page", rawPage);
  const goToPage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); };

  /**
   * 페이지 링크 주소. 목록을 가르는 값(카테고리·페이지)만 남긴다 — 지금 쿼리를
   * 통째로 복사하면 유입 추적 파라미터까지 모든 페이지 링크에 실려, 크롤러가
   * 같은 목록의 변형 주소를 잔뜩 보게 된다.
   *
   * pathname 이 아니라 공개 주소에서 만든다. rewrite 뒤라 pathname 이 내부
   * 경로(/main/...)일 수 있다.
   */
  const pageHref = (p) => {
    const sp = new URLSearchParams();
    const catId = searchParams?.get("catId");
    const category = searchParams?.get("category");
    if (catId) sp.set("catId", catId);
    else if (category) sp.set("category", category);
    if (p > 1) sp.set("page", String(p));
    const qs = sp.toString();
    const base = withLocaleHref("/clinicPrice", lang);
    return qs ? `${base}?${qs}` : base;
  };

  /**
   * 페이지 링크 클릭. 평소에는 지금처럼 화면 안에서 처리하고(이동 없음),
   * 새 탭·새 창으로 여는 조작은 링크 본래 동작에 맡긴다.
   */
  const onPageLinkClick = (e, p) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey
    ) return;
    e.preventDefault();
    goToPage(p);
  };

  const catResetRef = useRef(true);
  const searchResetRef = useRef(true);

  /* SSR mode: 라우터 네비게이션 후 서버에서 새 데이터가 내려올 때 동기화 */
  useEffect(() => {
    if (!hasSSRData) return;
    setItems(initialItems);
    if (initialSelectedCatId) setSelectedCatId(initialSelectedCatId);
  }, [initialItems, initialSelectedCatId, hasSSRData]);

  /* 카테고리가 바뀌면 1페이지로 돌린다 (#905).
   *
   * 전에는 이 초기화가 아래 "상품 로드(CSR only)" effect 안에 있었다. 그 effect 는
   * `if (hasSSRData) return;` 으로 시작하므로 서버 데이터를 쓰는 지점(구로)에서는
   * 아예 실행되지 않았다 — 스킨케어 2페이지를 보다 다른 카테고리를 고르면 page 가
   * 2 로 남아 "검색 결과가 없습니다" 가 떴다.
   *
   * 첫 실행에서는 리셋하지 않는다. usePageParam 이 URL 의 ?page=N 을 복원하는데
   * 여기서 1 로 덮으면 상세 → 뒤로가기 때 보던 페이지가 사라진다.
   * selectedCatId 가 비어 있는 동안에는 가드를 소비하지 않는다 — CSR 모드는
   * null 로 시작해서, 카테고리가 정해지는 순간이 "첫 실행" 이어야 한다. */
  useEffect(() => {
    if (!selectedCatId) return;
    if (catResetRef.current) {
      catResetRef.current = false;
      return;
    }
    setPage(1);
    // setPage 는 usePageParam 이 돌려주는 세터로 아이덴티티가 고정이다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatId]);

  /* 1) 카테고리 로드 (CSR mode only) */
  useEffect(() => {
    if (hasSSRData) return;
    (async () => {
      const snap = await getDocs(collection(db, branchId, "categories", "list"));
      const cats = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((d) => d.order !== undefined)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((d) => ({
          id: d.id,
          name: d.name ?? { ko: "" },
          shortName: d.shortName ?? d.shortLabel ?? null,
          order: d.order ?? 0,
        }));

      const allCat = {
        id: "ALL",
        name: { ko: "전체", en: "All", ja: "すべて", zh: "全部", th: "ทั้งหมด" },
        shortName: { ko: "전체보기", en: "View All", ja: "すべて", zh: "全部", th: "ทั้งหมด" },
        order: -9999,
      };

      const finalCats = [allCat, ...cats];
      setCategories(finalCats);

      let next = null;
      if (rawCatId && finalCats.some((c) => c.id === rawCatId)) {
        next = rawCatId;
      } else if (rawCatName) {
        next = findCatIdByAnyName(finalCats, rawCatName);
      }

      if (!next) next = "ALL";
      setSelectedCatId(next);
    })();
  }, [branchId]);

  /* 1-b) CSR mode: URL의 catId가 바뀌면 selectedCatId 동기화 (초기 로드 이후) */
  useEffect(() => {
    if (hasSSRData) return;
    if (!categories.length) return;
    const next = rawCatId && categories.some((c) => c.id === rawCatId) ? rawCatId : "ALL";
    setSelectedCatId(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawCatId]);

  /* 2) 선택된 카테고리의 상품 로드 (CSR mode only) */
  useEffect(() => {
    if (hasSSRData) return;
    if (!selectedCatId) {
      setItems([]);
      return;
    }
    (async () => {
      const colRef = collection(db, branchId, "product", "proucts");

      let q;
      if (selectedCatId === "ALL") {
        q = query(colRef, where("isPublished", "==", true));
      } else {
        q = query(
          colRef,
          where("isPublished", "==", true),
          where("categoryId", "==", selectedCatId)
        );
      }

      const koName =
        selectedCatId !== "ALL"
          ? getLocaleString(categories.find((c) => c.id === selectedCatId)?.name, "ko")
          : null;

      const bag = new Map();

      try {
        const s1 = await getDocs(q);
        s1.forEach((d) => bag.set(d.id, { id: d.id, ...d.data() }));
      } catch (e) {
        console.error(e);
      }

      if (koName) {
        const qByName = query(colRef, where("isPublished", "==", true), where("category", "==", koName));
        try {
          const s2 = await getDocs(qByName);
          s2.forEach((d) => bag.set(d.id, { id: d.id, ...d.data() }));
        } catch {}
      }

      setItems(Array.from(bag.values()));
    })();
  }, [selectedCatId, categories, branchId]);

  useEffect(() => {
    if (searchResetRef.current) {
      searchResetRef.current = false;
    } else {
      setPage(1);
    }
  }, [search, sortType]);

  /* 3) 검색/정렬 */
  const sorted = useMemo(() => {
    let list = [...items];

    if (search.trim()) {
      const key = search.trim().toLowerCase();
      list = list.filter((it) => {
        const nm = getLocaleString(it.name, lang).toLowerCase();
        const catNm = getLocaleString(it.categoryName ?? { ko: it.category }, lang).toLowerCase();
        const dNames = asArray(it.detailProducts).map((d) =>
          getLocaleString(d?.name, lang).toLowerCase()
        );
        return nm.includes(key) || catNm.includes(key) || dNames.some((x) => x.includes(key));
      });
    }

    const priceKey = (it) => {
      const v = minDiscount(it.detailProducts);
      return Number.isFinite(v) && v > 0 ? v : null;
    };

    switch (sortType) {
      case "highPrice":
        list.sort((a, b) => {
          const av = priceKey(a);
          const bv = priceKey(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return bv - av;
        });
        break;
      case "lowPrice":
        list.sort((a, b) => {
          const av = priceKey(a);
          const bv = priceKey(b);
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return av - bv;
        });
        break;
      case "recent":
      default:
        if (selectedCatId === "ALL") {
          list.sort((a, b) => (a.allTabOrder ?? 9999) - (b.allTabOrder ?? 9999));
        } else {
          list.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        }
        break;
    }
    return list;
  }, [items, search, sortType, lang, selectedCatId]);

  /* 4) 페이지네이션 */
  const totalPages = Math.max(1, Math.ceil(sorted.length / ITEMS_PER_PAGE));

  /* 범위를 벗어난 페이지는 보정해서 그린다 (#905).
   * 카테고리 전환 외에도 같은 증상을 만드는 길이 있다 — 검색으로 결과가 줄어들 때,
   * 관리자가 상품을 지울 때, 누가 ?page=99 링크를 공유할 때. 그대로 자르면 빈
   * 목록이 되고 pager 까지 사라져 아무 데도 못 간다. */
  const safePage = Math.min(Math.max(1, page), totalPages);

  const paginatedItems = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return sorted.slice(start, start + ITEMS_PER_PAGE);
  }, [sorted, safePage]);

  /* handlers */
  const handleCategoryClick = (catId) => {
    setCatSheetOpen(false);
    const koLabel = getLocaleString(categories.find((c) => c.id === catId)?.name, "ko");
    router.push(
      withLocaleHref(
        `/clinicPrice?catId=${encodeURIComponent(catId)}&category=${encodeURIComponent(koLabel)}`,
        lang
      )
    );
  };

  const selectedCatLabel = getCatLabel(
    categories.find((c) => c.id === selectedCatId),
    lang
  );

  return (
    <div>
      {/* 상단 사진 없이 바로 흰 화면 — 고정 헤더(모바일 55px·PC 74px) 높이만큼만 비운다 */}
      <div aria-hidden="true" className="h-[56px] lg:h-[74px]" />

      {/* Content */}
      <div className="px-6 sm:px-10 py-8 lg:py-24 max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* Sidebar Navigation */}
          <div className="hidden lg:block w-[240px] flex-shrink-0">
            <div className="flex flex-col">
              <div className="flex flex-col gap-1">
                {categories.map((cat) => {
                  const full = getCatLabel(cat, lang);
                  const isActive = selectedCatId === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryClick(cat.id)}
                      className={`group flex w-full items-center justify-between py-3 text-left transition-all duration-300 border-b border-[#eee] last:border-0
                        ${isActive ? "text-[#181818] font-bold pl-2" : "text-[#555] hover:text-[#333] hover:pl-2"}`}
                      title={full}
                    >
                      <span className="text-lg tracking-tight">{full}</span>
                      {isActive && <div className="h-2 w-2 rounded-full bg-[#FFC0CB]" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main */}
          <div className="flex-1 flex flex-col gap-8 lg:gap-12">
            <div className="flex flex-col gap-8">
              {/* 페이지 대표 제목 — 선택한 카테고리 이름. 모바일은 화면에서만 숨겨 카테고리 버튼부터 (검색엔진은 읽는다) */}
              <div className="sr-only lg:not-sr-only">
                <h1 className="flex items-end gap-2 text-xl font-bold lg:text-4xl marFont text-[#181818]">
                  {selectedCatLabel || t(UI.heading, lang)}
                </h1>
                <p className="hidden text-[#666] mt-2 font-light tracking-wide lg:block lg:text-lg">
                  {t(UI.subline, lang)}
                </p>
              </div>

              {/* Mobile: 카테고리 그리드 */}
              <div className="lg:hidden w-full" data-aos="fade-up">
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => {
                    const full = getCatLabel(cat, lang);
                    const isActive = selectedCatId === cat.id;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => handleCategoryClick(cat.id)}
                        className={`w-full rounded-xl border px-2 py-3 text-xs font-medium transition flex items-center justify-center text-center break-keep min-h-[44px]
                          ${isActive
                            ? "border-[#FFC0CB] bg-[#FFC0CB] text-[#181818] shadow-sm font-bold"
                            : "border-[#e0e0e0] bg-white text-[#666] hover:border-[#bbb]"
                          }`}
                      >
                        {full}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Sort & Search */}
              <div className="flex flex-row items-center justify-between gap-3 lg:gap-4">
                <div className="flex shrink-0 gap-1.5 lg:gap-2">
                  {["recent", "highPrice", "lowPrice"].map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setSortType(mode)}
                      className={`px-2.5 py-1 text-[11px] lg:px-4 lg:py-1.5 lg:text-xs font-medium rounded-full border transition-all ${
                        sortType === mode
                          ? "border-[#181818] bg-[#181818] text-white"
                          : "border-[#e0e0e0] bg-white text-[#555] hover:border-[#555] hover:bg-gray-50"
                      }`}
                    >
                      {t(mode === "recent" ? UI.sortRecent : mode === "highPrice" ? UI.sortHigh : UI.sortLow, lang)}
                    </button>
                  ))}
                </div>

                <div className="relative group min-w-0 flex-1 lg:w-auto lg:flex-none">
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full lg:w-[240px] border-b border-[#ddd] bg-transparent py-2 pl-0 pr-8 text-sm outline-none transition-colors focus:border-[#181818] placeholder:text-[#ccc]"
                    placeholder={t(UI.searchPh, lang)}
                  />
                  <svg
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div className="hidden lg:block w-full h-px bg-[#eee]" />

            {/* Product Grid */}
            {paginatedItems.length === 0 ? (
              <div className="w-full flex-grow min-h-[400px] py-20 text-center text-gray-500 flex items-center justify-center">
                {t(UI.noResults, lang)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-5 gap-y-10 lg:gap-y-14">
                  {paginatedItems.map((item, idx) => {
                    const title = getLocaleString(item.name, lang);
                    const catLabel = getLocaleString(item.categoryName ?? { ko: item.category }, lang);
                    const d0 = firstDetail(item.detailProducts);
                    const sub = getLocaleString(d0?.name, lang);
                    const price = minDiscount(item.detailProducts);
                    const imgSrc = pickImageByLocale(item, lang);

                    return (
                      // 진짜 링크여야 검색엔진이 개별 시술로 따라 들어간다.
                      // 예전에는 div + onClick 이라, 목록이 보여도 상세로 가는
                      // 길이 없었다. prefetch 는 꺼서 기존 네트워크 동작을 지킨다.
                      <Link
                        key={item.id}
                        href={withLocaleHref(`/clinicPrice/${item.id}`, lang)}
                        prefetch={false}
                        className="group flex flex-col gap-4 cursor-pointer"
                        data-aos="fade-up"
                        data-aos-delay={(idx % 4) * 50}
                      >
                        <div className="relative w-full aspect-square overflow-hidden rounded-2xl bg-[#f8f8f8]">
                          <Image
                            src={imgSrc}
                            alt={title || "item"}
                            fill
                            className="object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/5 pointer-events-none" />
                        </div>
                        <div className="flex flex-col px-1">
                          <span className="text-[10px] lg:text-xs font-bold uppercase tracking-widest text-[#555] mb-1">
                            {catLabel}
                          </span>
                          <h4 className="text-base lg:text-lg font-bold text-[#181818] leading-tight mb-1 group-hover:underline decoration-1 underline-offset-4 line-clamp-2 min-h-[1.5em]">
                            {title}
                          </h4>
                          {sub && (
                            <p className="text-xs lg:text-sm text-[#555] line-clamp-1 mb-3">{sub}</p>
                          )}
                          <p className="text-lg lg:text-xl font-bold text-[#181818] mt-auto text-right">
                            {formatPriceKRW(price, lang)}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <div className="mt-12 flex justify-center items-center gap-2">
                    {/* 페이지 이동도 진짜 링크여야 13번째 이후 시술이 발견된다.
                        사람에게는 지금과 똑같이 동작한다 — 클릭은 가로채서
                        화면 안에서 처리하고(#905 의 safePage 그대로 사용),
                        새 탭으로 여는 조작만 링크에 맡긴다.
                        갈 곳이 없는 이전·다음은 링크가 아니라 비활성 버튼으로 둔다. */}
                    {safePage === 1 ? (
                      <button
                        disabled
                        aria-label={t(UI.prevPage, lang)}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 opacity-30 cursor-not-allowed"
                      >
                        <ChevronLeft size={20} />
                      </button>
                    ) : (
                      <a
                        href={pageHref(safePage - 1)}
                        onClick={(e) => onPageLinkClick(e, safePage - 1)}
                        aria-label={t(UI.prevPage, lang)}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
                      >
                        <ChevronLeft size={20} />
                      </a>
                    )}

                    {Array.from({ length: totalPages }).map((_, i) => {
                      const p = i + 1;
                      const isCurr = p === safePage;
                      return (
                        <a
                          key={p}
                          href={pageHref(p)}
                          onClick={(e) => onPageLinkClick(e, p)}
                          aria-label={`${t(UI.pageNumber, lang)} ${p}`}
                          aria-current={isCurr ? "page" : undefined}
                          className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold transition-all ${
                            isCurr ? "bg-[#181818] text-white" : "text-[#666] hover:bg-gray-100"
                          }`}
                        >
                          {p}
                        </a>
                      );
                    })}

                    {safePage === totalPages ? (
                      <button
                        disabled
                        aria-label={t(UI.nextPage, lang)}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 opacity-30 cursor-not-allowed"
                      >
                        <ChevronRight size={20} />
                      </button>
                    ) : (
                      <a
                        href={pageHref(safePage + 1)}
                        onClick={(e) => onPageLinkClick(e, safePage + 1)}
                        aria-label={t(UI.nextPage, lang)}
                        className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50"
                      >
                        <ChevronRight size={20} />
                      </a>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <MobileCategorySheet
        open={catSheetOpen}
        onClose={() => setCatSheetOpen(false)}
        categories={categories}
        selectedCatId={selectedCatId}
        lang={lang}
        onPick={handleCategoryClick}
      />
    </div>
  );
}
